import { authHeader, legacyBearerAllowed, queryToken, useAuth, type Auth } from '../auth/store';
import type { CmdResult, Me } from './types';

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
  get notFound() {
    return this.status === 404;
  }
}

function errorMessage(status: number, statusText: string, data: unknown): string {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    const e = d.error;
    if (e && typeof e === 'object') {
      const eo = e as Record<string, unknown>;
      if (typeof eo.message === 'string') return eo.code ? `${String(eo.code)}: ${eo.message}` : eo.message;
    }
    if (typeof e === 'string') return e;
    if (typeof d.detail === 'string') return d.detail;
    if (typeof d.message === 'string') return d.message;
  }
  if (typeof data === 'string' && data && data.length < 200) return data;
  return `${status} ${statusText}`.trim();
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

let refreshing: Promise<boolean> | null = null;

async function refreshAccess(): Promise<boolean> {
  const a = useAuth.getState().auth;
  if (a?.kind !== 'jwt') return false;
  refreshing ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh: a.refresh }),
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { access?: string; refresh?: string };
      if (!data.access) return false;
      const cur = useAuth.getState().auth;
      if (cur?.kind === 'jwt') useAuth.getState().setAuth({ ...cur, access: data.access, refresh: data.refresh ?? cur.refresh });
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

/** Make sure a JWT access token is fresh enough (used before opening the WebSocket). */
export async function ensureFreshAccess(): Promise<void> {
  const a = useAuth.getState().auth;
  if (a?.kind !== 'jwt') return;
  const exp = jwtExp(a.access);
  if (exp && exp * 1000 - Date.now() < 30_000) await refreshAccess();
}

function jwtExp(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

export interface RequestOpts {
  signal?: AbortSignal;
  /** Don't log out on 401 (used by login probes). */
  noAuthRedirect?: boolean;
  auth?: Auth | null;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown, opts: RequestOpts = {}): Promise<T> {
  const doFetch = () => {
    const a = opts.auth !== undefined ? opts.auth : useAuth.getState().auth;
    const headers: Record<string, string> = {};
    const h = authHeader(a);
    if (h) headers.authorization = h;
    let payload: BodyInit | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      headers['content-type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    return fetch(path, { method, headers, body: payload, signal: opts.signal });
  };
  let res = await doFetch();
  if (res.status === 401 && opts.auth === undefined && (await refreshAccess())) res = await doFetch();
  const data = await parse(res);
  if (res.status === 401 && !opts.noAuthRedirect && opts.auth === undefined) {
    useAuth.getState().setAuth(null);
  }
  if (!res.ok) throw new ApiError(res.status, errorMessage(res.status, res.statusText, data), data);
  return data as T;
}

export const get = <T>(path: string, opts?: RequestOpts) => api<T>('GET', path, undefined, opts);
export const post = <T>(path: string, body?: unknown, opts?: RequestOpts) => api<T>('POST', path, body ?? {}, opts);
export const put = <T>(path: string, body?: unknown) => api<T>('PUT', path, body ?? {});
export const patch = <T>(path: string, body?: unknown) => api<T>('PATCH', path, body ?? {});
export const del = <T>(path: string) => api<T>('DELETE', path);

/** Upload with progress (XHR, since fetch has no upload progress). */
export function upload<T>(path: string, form: FormData, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    const h = authHeader(useAuth.getState().auth);
    if (h) xhr.setRequestHeader('authorization', h);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: unknown = xhr.responseText;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* text */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, errorMessage(xhr.status, xhr.statusText, data), data));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network error', null));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', null));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(form);
  });
}

/** URL for media elements (<video>/<audio>) that cannot send headers: appends ?token=. */
export function withToken(url: string): string {
  const t = queryToken(useAuth.getState().auth);
  if (!t) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(t)}`;
}

/** Fetch a protected binary (map image, snapshot) as an object URL. */
export async function fetchBlobUrl(path: string, signal?: AbortSignal): Promise<string> {
  const doFetch = () => {
    const h = authHeader(useAuth.getState().auth);
    return fetch(path, { headers: h ? { authorization: h } : {}, signal });
  };
  let res = await doFetch();
  if (res.status === 401 && (await refreshAccess())) res = await doFetch();
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`, null);
  return URL.createObjectURL(await res.blob());
}

// ---------------------------------------------------------------- auth flows

export interface LoginResponse {
  access: string;
  refresh: string;
  user?: { username?: string };
}

export async function loginPassword(username: string, password: string): Promise<Auth> {
  const r = await api<LoginResponse>('POST', '/api/auth/login', { username, password }, { auth: null, noAuthRedirect: true });
  return { kind: 'jwt', access: r.access, refresh: r.refresh, username: r.user?.username ?? username };
}

/** Validate an API token: `Token <t>` per protocol; dev fallback `Bearer <t>` for the Node reference. */
export async function loginToken(token: string): Promise<Auth> {
  const primary: Auth = { kind: 'token', token, scheme: 'Token' };
  try {
    await api<Me>('GET', '/api/me', undefined, { auth: primary, noAuthRedirect: true });
    return primary;
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401 || !legacyBearerAllowed) throw e;
  }
  const legacy: Auth = { kind: 'token', token, scheme: 'Bearer' };
  await api<Me>('GET', '/api/me', undefined, { auth: legacy, noAuthRedirect: true });
  return legacy;
}

// ---------------------------------------------------------------- robot commands

/** POST /api/robots/:serial/cmd — resolves with the robot result (ok or not); throws only on transport errors. */
export async function robotCmd<T = Record<string, unknown>>(serial: string, name: string, args: Record<string, unknown> = {}, timeoutMs?: number): Promise<CmdResult<T>> {
  try {
    return await api<CmdResult<T>>('POST', `/api/robots/${encodeURIComponent(serial)}/cmd`, { name, args, ...(timeoutMs ? { timeoutMs } : {}) });
  } catch (e) {
    if (e instanceof ApiError && e.data && typeof e.data === 'object' && 'ok' in (e.data as object)) return e.data as CmdResult<T>;
    throw e;
  }
}
